// Copyright (c) 2019-present Ladifire, Inc. All Rights Reserved.
// See LICENSE.txt for license information.
import React, {PureComponent} from 'react';
import {Transition} from 'react-transition-group';
import classNames from 'classnames';

// import {SIZES_MAP, DEFAULT_SIZE} from './constants';
import {SpinnerProps, SpinnerState} from '../types';

import styles from '../../../styles/components/spinner/style.scss';

export default class Spinner extends PureComponent<SpinnerProps, SpinnerState> {
    static defaultProps = {
        delay: 100,
        isCompleting: false,
        invertColor: false,
        onComplete: () => {},
        size: 'medium',
        center: false,
    };

    transitionNode: Transition | null = null;

    constructor(props: SpinnerProps) {
        super(props);
        this.state = {
            phase: '',
        };
    }

    enter = () => {
        const {delay} = this.props;
        if (delay) {
            this.setState({phase: 'DELAY'});
        } else {
            this.setState({phase: 'ENTER'});
        }
    };

    idle = () => {
        this.setState({phase: 'IDLE'});
    };

    exit = () => {
        this.setState({phase: 'LEAVE'});
    };

    endListener = (node: HTMLElement, done: () => void) => {
        const executeCallback = (event: Event): void => {
            // ignore animation events on the glyph

            if ((event.target as SVGElement).tagName === 'svg') {
                return;
            }
            if (this.state.phase === 'DELAY') {
                this.setState({phase: 'ENTER'});
                this.endListener(node, done);
            } else {
                done();
            }
            // eslint-disable-next-line consistent-return
            return node && node.removeEventListener('animationend', executeCallback);
        };

        // FIX - jest-emotion doesn't recognise the DOM node so it can't add
        // the eventListener in the @atlaskit/button tests.
        // Should be fixed when we move to emotion@10
        if (node && node.addEventListener) {
            return node.addEventListener('animationend', executeCallback);
        }
        return done();
    };

    render() {
        const {phase} = this.state;
        const {
            delay,
            invertColor,
            isCompleting,
            center,
            size,
            testId,
        } = this.props;

        return (
            <div
                className={classNames(styles.spinner__wrapper, {
                    [styles.spinner__center]: center,
                    [styles[`spinner__size_${size}`]]: true,
                    [styles.spinner__delay]: phase === 'DELAY' && delay > 0,
                })}
            >
                <Transition
                    addEndListener={this.endListener}
                    appear
                    in={!isCompleting}
                    mountOnEnter
                    unmountOnExit
                    onEnter={this.enter}
                    onEntered={this.idle}
                    onExit={this.exit}
                    onExited={() => this.props.onComplete()}
                    timeout={0}
                    ref={(node) => {
                        this.transitionNode = node;
                    }}
                >
                    <div>
                        <div className={classNames(styles.spinner__track)}/>
                        <div className={classNames(styles.spinner__fills)}>
                            <div className={classNames(styles.spinner__fill_mark1)}>
                                <div className={classNames(styles.spinner__fill_sub_mark1)}>
                                    <div className={classNames(styles.spinner__fill)}/>
                                </div>
                            </div>
                            <div className={classNames(styles.spinner__fill_mark2)}>
                                <div className={classNames(styles.spinner__fill_sub_mark2)}>
                                    <div className={classNames(styles.spinner__fill)}/>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>
            </div>
        );
    }
}
